import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Bird, X, Mic, Square, Send, Loader2, Sparkles, CalendarClock, Video, Phone, Users,
  ListTodo, Target, HelpCircle, ClipboardCheck, Wrench, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import LiveWaveform from "@/components/LiveWaveform";
import ProjectDraftReview from "@/components/ProjectDraftReview";
import { getMode } from "@/lib/mode";

const TYPE_ICON = { "Présentiel": Users, "Visio": Video, "Appel": Phone };
function fmtWhen(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDue(iso) {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return iso; }
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function LeadDraftCard({ lead }) {
  const navigate = useNavigate();
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.company || "Prospect";
  const open = () => {
    window.dispatchEvent(new CustomEvent("birdie:close"));
    navigate("/lead/new", { state: { captureMethod: "VOICE", eventName: localStorage.getItem("klt_event") || "", contactInfo: { firstName: lead.firstName || "", lastName: lead.lastName || "", company: lead.company || "", role: lead.role || "", phone: lead.phone || "", email: lead.email || "" }, voiceLead: lead } });
  };
  return (
    <div className="mt-2 rounded-xl border border-violet-500/30 bg-background p-3 space-y-1 text-xs" data-testid="lead-draft-card">
      <p className="font-bold text-sm">{name}{lead.company && name !== lead.company ? ` · ${lead.company}` : ""}</p>
      {lead.project && <p><b>Projet :</b> {lead.project}</p>}
      {lead.budget && <p><b>Budget :</b> {lead.budget}</p>}
      {lead.phone && <p className="text-muted-foreground">{lead.phone}</p>}
      <button onClick={open} data-testid="lead-draft-open" className="mt-2 w-full h-9 rounded-lg bg-primary text-primary-foreground font-bold">Ouvrir et enregistrer la fiche</button>
    </div>
  );
}

function ActionCard({ msg }) {
  if (msg.kind === "project_draft" && msg.draft) return <ProjectDraftReview draft={msg.draft} />;
  if (msg.kind === "lead_draft" && msg.lead) return <LeadDraftCard lead={msg.lead} />;
  if (msg.kind === "brief" && msg.brief) {
    const b = msg.brief;
    const has = (b.appointments?.length || b.overdueTasks?.length || b.todayTasks?.length);
    if (!has) return null;
    return (
      <div className="mt-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-2.5 space-y-2" data-testid="birdie-brief-card">
        {b.appointments?.length > 0 && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-sky-700 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> RDV du jour</p>
            <ul className="mt-1 space-y-0.5">
              {b.appointments.map((a, i) => {
                const Icon = TYPE_ICON[a.type] || CalendarClock;
                return <li key={i} className="text-xs flex items-center gap-1.5"><Icon className="w-3 h-3 text-sky-600 shrink-0" /><b>{fmtTime(a.startAt)}</b> {a.title}</li>;
              })}
            </ul>
          </div>
        )}
        {b.overdueTasks?.length > 0 && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-red-600 flex items-center gap-1"><ListTodo className="w-3 h-3" /> En retard</p>
            <ul className="mt-1 space-y-0.5">{b.overdueTasks.map((t, i) => <li key={i} className="text-xs flex gap-1.5"><span className="text-red-500">•</span> {t.title}</li>)}</ul>
          </div>
        )}
        {b.todayTasks?.length > 0 && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 flex items-center gap-1"><ListTodo className="w-3 h-3" /> Tâches du jour</p>
            <ul className="mt-1 space-y-0.5">{b.todayTasks.map((t, i) => <li key={i} className="text-xs flex gap-1.5"><span className="text-emerald-500">•</span> {t.title}</li>)}</ul>
          </div>
        )}
      </div>
    );
  }
  if (msg.kind === "appointment" && msg.appointment) {
    const a = msg.appointment;
    const Icon = TYPE_ICON[a.type] || CalendarClock;
    return (
      <div className="mt-2 rounded-lg border border-sky-500/25 bg-sky-500/5 p-2.5" data-testid="birdie-appt-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-sky-800"><Icon className="w-4 h-4 text-sky-600" /> {a.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{fmtWhen(a.startAt)} · {a.type}</div>
        {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="text-xs text-sky-600 underline inline-flex items-center gap-1 mt-1"><Video className="w-3 h-3" /> Rejoindre la visio</a>}
      </div>
    );
  }
  if (msg.kind === "task" && msg.task) {
    return (
      <div className="mt-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-2.5 flex items-center gap-2 text-sm" data-testid="birdie-task-card">
        <ListTodo className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="flex-1">{msg.task.title}</span>
        {msg.task.dueDate && <span className="text-xs font-bold text-emerald-700 bg-white rounded px-1.5 py-0.5">{fmtDue(msg.task.dueDate)}</span>}
      </div>
    );
  }
  if (msg.kind === "prep" && msg.prep) {
    const blocks = [
      { key: "objectives", label: "Objectifs", icon: Target },
      { key: "questions", label: "Questions", icon: HelpCircle },
      { key: "toVerify", label: "À préparer", icon: ClipboardCheck },
      { key: "logistics", label: "Logistique", icon: Wrench },
    ];
    return (
      <div className="mt-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-2.5" data-testid="birdie-prep-card">
        {msg.prep.target && <div className="text-sm font-semibold text-emerald-800">{msg.prep.target}</div>}
        {msg.prep.contextRecap && <p className="text-xs text-muted-foreground italic mb-1.5">{msg.prep.contextRecap}</p>}
        {blocks.map((b) => {
          const items = msg.prep[b.key] || [];
          if (!items.length) return null;
          const Icon = b.icon;
          return (
            <div key={b.key} className="mt-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 flex items-center gap-1"><Icon className="w-3 h-3" /> {b.label}</p>
              <ul className="mt-0.5">{items.map((it, i) => <li key={i} className="text-xs flex gap-1.5"><span className="text-emerald-500">•</span> {it}</li>)}</ul>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
}

function BirdieChat({ onClose, initialBrief, autoRecord }) {
  const navigate = useNavigate();
  const [sessionId] = useState(() => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const [messages, setMessages] = useState(() => (
    initialBrief
      ? [{ role: "assistant", text: initialBrief.greeting, kind: "brief", brief: initialBrief }]
      : [{ role: "assistant", text: getMode() === "salon" ? "Coucou, moi c'est Birdie 🐦 ! Dictez votre prospect : « Julien Morel, Habitat Plus, 06 12 34 56 78, rénovation de cuisine, budget 15 000 € » — je prépare sa fiche." : "Coucou, moi c'est Birdie 🐦 ! Dictez un projet complet : « Salle de bain chez Mme Martin, 4 500 €, devis signé, du 15 au 17 juin, acompte 30 % » — je crée le client, le devis, le planning et l'échéancier." }]
  ));
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);
  const meta = () => ({ nowISO: new Date().toISOString(), tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris", sessionId });

  const pushResult = (data) => {
    setMessages((m) => [...m, { role: "assistant", text: data.reply, kind: data.kind, appointment: data.appointment, task: data.task, draft: data.draft, lead: data.lead, prep: { ...(data.prep || {}), target: data.target } }]);
    if (data.kind === "appointment" || data.kind === "task") window.dispatchEvent(new CustomEvent("birdie:changed"));
  };

  const sendText = async () => {
    const message = text.trim();
    if (!message || sending) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setText(""); setSending(true);
    try {
      const { data } = await api.post("/assistant/command", { message, ...meta() });
      pushResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Birdie est indisponible");
      setMessages((m) => [...m, { role: "assistant", text: "Oups, je n'ai pas pu traiter ça. Réessayez ?" }]);
    } finally { setSending(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
      let mime = "";
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
        mime = candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      }
      const opts = { audioBitsPerSecond: 32000 };
      if (mime) opts.mimeType = mime;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const actualType = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualType });
        if (blob.size < 1200) { toast.error("Enregistrement trop court — maintenez et parlez un instant."); return; }
        const ext = actualType.includes("mp4") ? "mp4" : actualType.includes("mpeg") ? "mp3" : actualType.includes("ogg") ? "ogg" : "webm";
        setSending(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `cmd.${ext}`);
          const m = meta();
          fd.append("nowISO", m.nowISO); fd.append("tz", m.tz); fd.append("sessionId", m.sessionId);
          const { data } = await api.post("/assistant/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.transcript) setMessages((mm) => [...mm, { role: "user", text: data.transcript }]);
          pushResult(data);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Transcription impossible");
        } finally { setSending(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch { toast.error("Micro inaccessible. Autorisez l'accès ou tapez votre demande."); }
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  useEffect(() => { if (autoRecord) { const t = setTimeout(() => startRec(), 300); return () => clearTimeout(t); } /* eslint-disable-next-line */ }, []);

  return (
    <div className="fixed right-3 sm:right-6 bottom-24 sm:bottom-6 z-[60] w-[calc(100vw-1.5rem)] sm:w-[380px] birdie-pop" data-testid="birdie-panel">
      <div className="rounded-2xl border border-emerald-500/30 bg-card shadow-2xl shadow-emerald-900/20 overflow-hidden flex flex-col" style={{ maxHeight: "72vh" }}>
        <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <Bird className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-heading font-bold leading-tight">Birdie</div>
            <div className="text-[11px] opacity-90 leading-tight">Votre assistant réunions & tâches</div>
          </div>
          <button onClick={onClose} data-testid="birdie-close" className="p-1 rounded-lg hover:bg-white/15 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-secondary/30">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`birdie-msg-${m.role}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-emerald-600 text-white rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                <div className={m.role === "assistant" ? "flex items-start gap-1.5" : ""}>
                  {m.role === "assistant" && m.kind && m.kind !== "chat" && <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />}
                  <span>{m.text}</span>
                </div>
                {m.role === "assistant" && <ActionCard msg={m} />}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start"><div className="bg-card border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-emerald-600" /> Birdie réfléchit…</div></div>
          )}
        </div>

        <div className="p-2.5 border-t border-border bg-card flex items-center gap-2">
          <button onClick={recording ? stopRec : startRec} disabled={sending} data-testid="birdie-mic" className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${recording ? "bg-rose-500 text-white animate-pulse" : "bg-secondary text-emerald-600 hover:bg-emerald-50"}`}>
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          {recording ? (
            <LiveWaveform stream={streamRef.current} active bars={20} className="flex-1 h-10" />
          ) : (
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText()}
              placeholder="Écrivez à Birdie…"
              disabled={sending}
              data-testid="birdie-input"
              className="flex-1 h-10 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          )}
          <button onClick={sendText} disabled={sending || recording || !text.trim()} data-testid="birdie-send" className="shrink-0 w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 disabled:opacity-50 transition-colors">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Birdie() {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState(null);
  const [autoRec, setAutoRec] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (!localStorage.getItem("klt_onboarded")) return; // let the first-login tutorial run first
    if (localStorage.getItem("klt_birdie_brief") === today) return;
    api.get("/assistant/daily-brief").then(({ data }) => {
      localStorage.setItem("klt_birdie_brief", today);
      if (data && data.greeting) { setBrief(data); setOpen(true); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const openHandler = () => { setAutoRec(false); setOpen(true); };
    const dictateHandler = () => { setAutoRec(true); setOpen(true); };
    const closeHandler = () => setOpen(false);
    window.addEventListener("birdie:open", openHandler);
    window.addEventListener("birdie:dictate", dictateHandler);
    window.addEventListener("birdie:close", closeHandler);
    return () => { window.removeEventListener("birdie:open", openHandler); window.removeEventListener("birdie:dictate", dictateHandler); window.removeEventListener("birdie:close", closeHandler); };
  }, []);

  const close = () => { setOpen(false); setBrief(null); setAutoRec(false); };
  return (
    <>
      {open && <BirdieChat onClose={close} initialBrief={brief} autoRecord={autoRec} />}
    </>
  );
}
